import { useState } from "react";
import { createManualTradingSession, useAction } from "wasp/client/operations";
import { Plus } from "lucide-react";
import { Button } from "../client/components/ui/button";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import { Textarea } from "../client/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { toast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import { SessionErrorSelect } from "./SessionErrorSelect";
import AddTradeModal from "../trading-journal/AddTradeModal";

// Fenêtre « Nouvelle session » (saisie d'une session passée), sortie de
// TradingSessionsPage pour être partagée avec le mode Lite. Le formulaire
// survit à la fermeture de la fenêtre, comme avant l'extraction.

interface AddSessionForm {
  startedAt: string;
  endedAt: string;
  mood: "excellent" | "good" | "neutral" | "poor" | "bad" | "";
  performance: "excellent" | "good" | "average" | "poor" | "";
  mentalState: string;
  notes: string;
  maxRunup: string;
  maxDrawdown: string;
  name: string;
  errorIds: string[];
}

const initialAddForm: AddSessionForm = {
  startedAt: "",
  endedAt: "",
  mood: "",
  performance: "",
  mentalState: "",
  notes: "",
  maxRunup: "",
  maxDrawdown: "",
  name: "",
  errorIds: [],
};

export function AddSessionDialog({ open, onOpenChange, onCreated }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const t = useTranslation();
  const isAddDialogOpen = open;
  const setIsAddDialogOpen = onOpenChange;
  const [addForm, setAddForm] = useState<AddSessionForm>(initialAddForm);
  const [isAddTradeModalOpen, setIsAddTradeModalOpen] = useState(false);
  const [addedTradesCount, setAddedTradesCount] = useState(0);
  const createManualSessionFn = useAction(createManualTradingSession);

  const handleAddSession = async () => {
    try {
      await createManualSessionFn({
        startedAt: addForm.startedAt ? new Date(addForm.startedAt) : new Date(),
        endedAt: addForm.endedAt ? new Date(addForm.endedAt) : new Date(),
        mood: addForm.mood as any,
        performance: addForm.performance as any,
        mentalState: addForm.mentalState || undefined,
        notes: addForm.notes || undefined,
        maxRunup: addForm.maxRunup !== "" ? parseFloat(addForm.maxRunup) : undefined,
        maxDrawdown: addForm.maxDrawdown !== "" ? parseFloat(addForm.maxDrawdown) : undefined,
        name: addForm.name.trim() || undefined,
        errorIds: addForm.errorIds,
      });
      toast({ title: t.session.sessionEnded });
      setIsAddDialogOpen(false);
      setAddForm(initialAddForm);
      setAddedTradesCount(0);
      onCreated();
    } catch (error: any) {
      toast({ title: t.common.error, description: error.message, variant: "destructive" });
    }
  };

  return (
    <>
      {/* Add Session Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t.sessions.addSession}</DialogTitle>
            <DialogDescription>{t.session.evaluation}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Name */}
            <div>
              <Label htmlFor="sessionName">{t.sessions.colName}</Label>
              <Input
                id="sessionName"
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value.slice(0, 100) })}
                placeholder={t.sessions.editNamePlaceholder}
                maxLength={100}
              />
              <p className="text-xs text-muted-foreground mt-1">{addForm.name.length}/100</p>
            </div>

            {/* Timing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="startedAt">{t.sessionDetail.startedAt}</Label>
                <Input
                  id="startedAt"
                  type="datetime-local"
                  value={addForm.startedAt}
                  onChange={(e) => setAddForm({ ...addForm, startedAt: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="endedAt">{t.sessionDetail.endedAt}</Label>
                <Input
                  id="endedAt"
                  type="datetime-local"
                  value={addForm.endedAt}
                  onChange={(e) => setAddForm({ ...addForm, endedAt: e.target.value })}
                />
              </div>
            </div>

            {/* Mood + Performance */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mood">{t.session.mood} *</Label>
                <Select
                  value={addForm.mood}
                  onValueChange={(value: AddSessionForm["mood"]) =>
                    setAddForm({ ...addForm, mood: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.session.moodPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">{t.session.moodExcellent}</SelectItem>
                    <SelectItem value="good">{t.session.moodGood}</SelectItem>
                    <SelectItem value="neutral">{t.session.moodNeutral}</SelectItem>
                    <SelectItem value="poor">{t.session.moodPoor}</SelectItem>
                    <SelectItem value="bad">{t.session.moodBad}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="performance">{t.session.performance} *</Label>
                <Select
                  value={addForm.performance}
                  onValueChange={(value: AddSessionForm["performance"]) =>
                    setAddForm({ ...addForm, performance: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.session.performancePlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">{t.session.perfExcellent}</SelectItem>
                    <SelectItem value="good">{t.session.perfGood}</SelectItem>
                    <SelectItem value="average">{t.session.perfAverage}</SelectItem>
                    <SelectItem value="poor">{t.session.perfPoor}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="mentalState">{t.session.mentalState}</Label>
              <Input
                id="mentalState"
                value={addForm.mentalState}
                onChange={(e) => setAddForm({ ...addForm, mentalState: e.target.value })}
                placeholder={t.session.mentalStatePlaceholder}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="maxRunup">{t.session.maxRunup}</Label>
                <Input
                  id="maxRunup"
                  type="number"
                  step="0.01"
                  value={addForm.maxRunup}
                  onChange={(e) => setAddForm({ ...addForm, maxRunup: e.target.value })}
                  placeholder={t.session.maxRunupPlaceholder}
                />
              </div>
              <div>
                <Label htmlFor="maxDrawdown">{t.session.maxDrawdown}</Label>
                <Input
                  id="maxDrawdown"
                  type="number"
                  step="0.01"
                  value={addForm.maxDrawdown}
                  onChange={(e) => setAddForm({ ...addForm, maxDrawdown: e.target.value })}
                  placeholder={t.session.maxDrawdownPlaceholder}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes">{t.session.notes}</Label>
              <Textarea
                id="notes"
                value={addForm.notes}
                onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                placeholder={t.session.notesPlaceholder}
                rows={4}
              />
            </div>

            {/* Errors */}
            <div>
              <Label>{t.session.errors}</Label>
              <SessionErrorSelect
                value={addForm.errorIds}
                onChange={(next) => setAddForm({ ...addForm, errorIds: next })}
              />
            </div>

            {/* Section trades */}
            <div className="pt-2 border-t border-border/50">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">{t.sessionDetail.trades}</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1.5 h-7 text-xs"
                  onClick={() => setIsAddTradeModalOpen(true)}
                >
                  <Plus className="w-3.5 h-3.5" />
                  {t.sessionDetail.addTrade}
                </Button>
              </div>
              {addedTradesCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  {t.sessions.tradesAddedCount.replace("{count}", String(addedTradesCount))}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddDialogOpen(false);
                setAddForm(initialAddForm);
                setAddedTradesCount(0);
              }}
            >
              {t.common.cancel}
            </Button>
            <Button onClick={handleAddSession} disabled={!addForm.mood || !addForm.performance}>
              {t.sessions.addSession}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AddTradeModal — nested, trades créés immédiatement en DB */}
      <AddTradeModal
        open={isAddTradeModalOpen}
        onOpenChange={setIsAddTradeModalOpen}
        onImportDone={() => {
          setIsAddTradeModalOpen(false);
          setAddedTradesCount((c) => c + 1);
        }}
      />

    </>
  );
}
